import { useMemo } from 'react';
import type { Course, Hole, Weather } from '../lib/types.ts';
import type { Fix } from '../lib/gps.ts';
import type { Parked } from '../lib/parked.ts';
import { bearingDeg, distanceM, formatDistance, playingDistance, toYards } from '../lib/geo.ts';
import { WEATHER_TEXT, windArrowDeg } from '../lib/weather.ts';
import { compassPoint, parkedInfo } from '../lib/parked.ts';

interface Props {
  course: Course;
  hole: Hole;
  holeIndex: number;
  teeColour: string;
  unit: 'm' | 'yd';
  fix: Fix | null;
  weather: Weather | null;
  parked: Parked | null;
  confidence: number;
  cartPathCount: number | null;
}

const Row = ({ k, v, note }: { k: string; v: string; note?: string }) => (
  <tr>
    <td className="name">{k}</td>
    <td>{v}</td>
    <td className="muted" style={{ textAlign: 'left', whiteSpace: 'normal' }}>
      {note ?? ''}
    </td>
  </tr>
);

/**
 * Everything the map is deciding, shown as numbers.
 *
 * The map deliberately shows one big number and hides the rest. This is where
 * the rest lives — every input to that number, so a golfer who wants to know
 * WHY it says 152 can see the flat distance, the elevation and the wind that
 * made it, rather than having to trust it.
 */
export default function AnalyticsPanel(props: Props) {
  const { course, hole, holeIndex, teeColour, unit, fix, weather, parked, confidence, cartPathCount } =
    props;

  const from = fix?.pos ?? hole.tees[teeColour] ?? hole.tees.White;
  const u = unit === 'yd' ? 'yd' : 'm';

  const d = useMemo(() => {
    const front = distanceM(from, hole.green.front);
    const middle = distanceM(from, hole.green.middle);
    const back = distanceM(from, hole.green.back);
    const shotBearing = bearingDeg(from, hole.green.middle);
    const rise =
      hole.greenElevation != null && hole.teeElevation != null
        ? hole.greenElevation - hole.teeElevation
        : 0;
    const play = playingDistance({
      flatM: middle,
      riseM: rise,
      windSpeed: weather?.windSpeed,
      windDirFrom: weather?.windDir,
      shotBearing,
    });
    return { front, middle, back, play, shotBearing, rise, depth: distanceM(hole.green.front, hole.green.back) };
  }, [from, hole, weather]);

  const conv = (m: number) => (unit === 'yd' ? toYards(m) : m);
  const pk = parked ? parkedInfo(parked, fix?.pos ?? null, hole.green.middle) : null;

  return (
    <div className="screen analytics">
      <div className="card">
        <h2>HOLE {hole.number}</h2>
        <table className="grid">
          <tbody>
            <Row k="Par" v={String(hole.par)} />
            <Row k="Stroke index" v={String(hole.strokeIndex)} note="hardest = 1" />
            <Row k="Card yardage" v={`${hole.yardage[teeColour] ?? hole.yardage.White} ${u}`} note={teeColour} />
            <Row k="Position on course" v={`${holeIndex + 1} of ${course.holes.length}`} />
            <Row
              k="Hole detection"
              v={`${Math.round(confidence * 100)}%`}
              note={confidence < 0.35 ? 'ambiguous — two holes are close together here' : 'confident'}
            />
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>DISTANCE TO GREEN</h2>
        <table className="grid">
          <tbody>
            <Row k="Front" v={`${formatDistance(d.front, unit)} ${u}`} note="nearest edge" />
            <Row k="Middle" v={`${formatDistance(d.middle, unit)} ${u}`} note="the number on the map" />
            <Row k="Back" v={`${formatDistance(d.back, unit)} ${u}`} note="far edge" />
            <Row k="Green depth" v={`${formatDistance(d.depth, unit)} ${u}`} note="front to back" />
            <Row
              k="Plays like"
              v={`${formatDistance(d.play.metres, unit)} ${u}`}
              note="flat distance adjusted for slope and wind"
            />
            <Row
              k="  from slope"
              v={`${d.play.elevationAdj >= 0 ? '+' : ''}${conv(d.play.elevationAdj).toFixed(1)} ${u}`}
              note={
                hole.greenElevation == null
                  ? 'no elevation data for this hole yet'
                  : `green is ${Math.abs(d.rise).toFixed(0)}m ${d.rise >= 0 ? 'above' : 'below'} the tee`
              }
            />
            <Row
              k="  from wind"
              v={`${d.play.windAdj >= 0 ? '+' : ''}${conv(d.play.windAdj).toFixed(1)} ${u}`}
              note={weather ? 'only the component along your shot line counts' : 'no weather yet'}
            />
            <Row k="Shot bearing" v={`${Math.round(d.shotBearing)}° ${compassPoint(d.shotBearing)}`} />
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>WEATHER</h2>
        {weather ? (
          <table className="grid">
            <tbody>
              <Row k="Conditions" v={WEATHER_TEXT[weather.code] ?? '—'} />
              <Row k="Temperature" v={`${weather.tempC.toFixed(1)} °C`} />
              <Row
                k="Wind"
                v={`${weather.windSpeed.toFixed(1)} m/s`}
                note={`${(weather.windSpeed * 3.6).toFixed(0)} km/h`}
              />
              <Row
                k="Wind from"
                v={`${Math.round(weather.windDir)}° ${compassPoint(weather.windDir)}`}
                note={`${Math.round(windArrowDeg(weather.windDir, d.shotBearing))}° relative to your shot`}
              />
              <Row k="Gusting" v={`${weather.gust.toFixed(1)} m/s`} />
              <Row k="Rain chance" v={`${weather.precipProb}%`} />
              <Row
                k="Updated"
                v={`${Math.round((Date.now() - weather.updatedAt) / 60000)} min ago`}
                note="refreshes every 10 minutes"
              />
            </tbody>
          </table>
        ) : (
          <div className="muted">No weather yet — it loads on the first fix.</div>
        )}
      </div>

      <div className="card">
        <h2>GPS</h2>
        <table className="grid">
          <tbody>
            {fix ? (
              <>
                <Row
                  k="Accuracy"
                  v={`±${Math.round(fix.accuracy)} m`}
                  note={fix.accuracy <= 10 ? 'good' : fix.accuracy <= 25 ? 'usable' : 'poor — stand clear of trees'}
                />
                <Row k="Speed" v={`${(fix.speed * 3.6).toFixed(1)} km/h`} />
                <Row k="Heading" v={`${Math.round(fix.heading)}° ${compassPoint(fix.heading)}`} />
                <Row k="Position" v={`${fix.pos[1].toFixed(5)}, ${fix.pos[0].toFixed(5)}`} />
                <Row
                  k="Fix age"
                  v={`${Math.round((Date.now() - fix.ts) / 1000)} s`}
                  note={fix.stale ? 'STALE — no recent fix' : 'live'}
                />
              </>
            ) : (
              <Row k="Status" v="no fix" note="waiting for satellites" />
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>BUGGY</h2>
        <table className="grid">
          <tbody>
            {pk ? (
              <>
                <Row k="Parked" v={`${pk.ageMin} min ago`} />
                <Row k="Buggy to green" v={`${formatDistance(pk.buggyToGreen, unit)} ${u}`} note="the shot from your clubs" />
                <Row k="You to buggy" v={`${formatDistance(pk.fromMe, unit)} ${u}`} />
                <Row k="Direction back" v={`${Math.round(pk.bearingToBuggy)}° ${compassPoint(pk.bearingToBuggy)}`} />
              </>
            ) : (
              <Row k="Parked" v="not marked" note="tap ? then Park here" />
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>COURSE</h2>
        <table className="grid">
          <tbody>
            <Row k="Name" v={course.name} />
            <Row k="Holes" v={String(course.holes.length)} />
            <Row k="Total par" v={String(course.holes.reduce((s, h) => s + h.par, 0))} />
            <Row
              k="Hazards mapped"
              v={String(course.holes.reduce((s, h) => s + h.hazards.length, 0))}
            />
            <Row k="Fenced zones" v={String(course.keepOut.length)} note="greens and tees" />
            <Row
              k="Cart paths"
              v={cartPathCount == null ? 'loading' : cartPathCount ? `${cartPathCount} segments` : 'none mapped'}
              note={cartPathCount === 0 ? 'this course has no paths in OpenStreetMap yet' : ''}
            />
            <Row
              k="Hole shape"
              v={hole.centreline && hole.centreline.length > 2 ? 'surveyed centreline' : 'straight line'}
              note={
                hole.centreline && hole.centreline.length > 2
                  ? 'corridor follows the real dogleg'
                  : 'no bend mapped for this hole'
              }
            />
            <Row k="Data source" v={course.source} />
          </tbody>
        </table>
      </div>
    </div>
  );
}
